import { Discipline } from '@centers/interfaces/discipline';
import { Contact } from '@contacts/interfaces/contact';
import {
  BookingFormActions,
  BookingFormGetters,
  BookingFormMutations,
} from './../interfaces/booking-form-store';
import { Module } from 'vuex';
import { RootState } from '@core/interfaces/root-state';
import {
  Booking,
  DateRange,
  DaysInput,
  ResourceSelectInfo,
  TimeRange,
} from '@bookings/interfaces/booking';
import {
  actionsFactory,
  gettersFactory,
  mutationsFactory,
  stateFactory,
} from '@shared/store/abstract-form.store';
import { Resource } from '@centers/interfaces/resource';
import { BookingEvent } from '@bookings/interfaces/booking-event';
import { BookingFormState } from '@bookings/interfaces/booking-form-store';

import BPCMessageService from '@core/services/bpc-messages.service';
import moment from 'moment';
import { setProp } from '@shared/helpers/operators';
import { Article } from '@accounting/interfaces/article';
import { PriceCategory } from '@contacts/interfaces/price-category';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import AuthorizationService from '@auth/services/authorization.service';

const abstract_state = stateFactory<Booking>(new Booking());
const abstract_getters = gettersFactory<Booking>();
const abstract_actions = actionsFactory<Booking>();
const abstract_mutations = mutationsFactory<Booking>(new Booking());

const bpcRouterHelper = new BPCApiRouteHelper();

const bpcMessage = new BPCMessageService();

const $authorizationService = new AuthorizationService();

const state: BookingFormState = {
  ...abstract_state,
  conflictChanged: false,
  eventChanged: false,
  checkingPrice: false,
  bookingFormModelChanged: false,
  isSearched: false,
  showReplaceButton: false,
  showConflicts: false,
  resourcesSelectInfo: [],
  // extends with new prop and methods here
};

const getters: BookingFormGetters = {
  ...abstract_getters,
  // Flags
  isSimpleBooking: (state: BookingFormState, getters): boolean => {
    return getters.repetitive === false && getters.resources.length <= 1;
  },
  isSearched: (state: BookingFormState): boolean => {
    return state.isSearched;
  },
  showInputContact: (state: BookingFormState, getters): boolean => {
    return getters.type === 'booking';
  },
  showInputDiscipline: (state: BookingFormState, getters): boolean => {
    return getters.type === 'booking';
  },
  canGetPrice: (state: BookingFormState, getters): boolean => {
    return (
      getters.type === 'booking' &&
      getters.bookingStartTime &&
      getters.bookingEndTime &&
      getters.bookingStartDate &&
      (!getters.repetitive || getters.bookingEndDate) &&
      getters.resources.length >= 1 &&
      (getters.isSearched || !getters.repetitive) &&
      !Number.isInteger(getters.article)
      // getters.priceCategoryId
    );
  },

  isMultiResource: (state: BookingFormState, getters): boolean => {
    return getters.resources.length > 1;
  },
  bookingFormModelRealChanged: (state: BookingFormState, getters): boolean => {
    return getters.isDirty && getters.bookingFormModelChanged;
  },
  bookingFormModelChanged: (state: BookingFormState): boolean => {
    return state.bookingFormModelChanged;
  },
  cantSaveBooking: (state: BookingFormState, getters): boolean => {
    return (
      !getters.formHasStartAndEndTime ||
      getters.isSaving ||
      getters.isSearching ||
      !getters.bookingStartDate ||
      (getters.repetitive && !getters.bookingEndDate) ||
      (getters.type === 'booking' && !getters.contactId && !state.model.contact_id) ||
      // (getters.type === 'booking' && !getters.priceCategoryId && !state.model.price_category_id) ||
      getters.resources.length < 1 ||
      (getters.type === 'booking' && getters.price < 0)
    );
  },

  cantSearchBooking: (state: BookingFormState, getters): boolean => {
    return (
      !getters.formHasStartAndEndTime ||
      getters.isSaving ||
      getters.isSearching ||
      !getters.bookingStartDate ||
      (getters.repetitive && !getters.bookingEndDate) ||
      //(getters.type === 'booking' && !getters.contactId) ||
      (getters.resources.length < 1 && !getters.discipline)
    );
  },

  formHasStartAndEndTime: (state: BookingFormState, getters): boolean => {
    return !!(getters.bookingStartTime && getters.bookingEndTime);
  },

  hasConflicts: (state: BookingFormState, getters): boolean => {
    return getters.conflicts.length > 0;
  },

  isMultiplayerAllowed: (state: BookingFormState, getters): boolean => {
    const discipline = getters.discipline;
    const isSimple = getters.isSimpleBooking;
    const isClientBooking = getters.type === 'booking';
    return isClientBooking && isSimple && discipline && discipline.enable_multiplayer_booking;
  },

  // Action management
  showSearchButton: (state: BookingFormState, getters): boolean => {
    return (
      !getters.showReplaceButton &&
      (getters.repetitive || (!getters.repetitive && getters.isMultiResource)) &&
      !getters.isSearched &&
      !getters.showCloseButton
    );
  },

  showSaveButton: (state: BookingFormState, getters): boolean => {
    return !getters.showSearchButton && !getters.showReplaceButton && !getters.showCloseButton;
  },

  showCloseButton: (state: BookingFormState, getters): boolean => {
    return getters.currentAction === 'edit';
  },

  showCancelButton: (state: BookingFormState, getters): boolean => {
    return getters.bookingStatus === 'draft' || getters.bookingStatus === 'confirmed';
  },

  showDeleteButton: (state: BookingFormState, getters): boolean => {
    return (
      getters.bookingStatus === 'draft' ||
      getters.bookingStatus === 'requested' ||
      getters.bookingStatus === 'refused' ||
      getters.bookingStatus === 'confirmed'
    );
  },

  showConfirmButton: (state: BookingFormState, getters): boolean => {
    return (
      getters.bookingStatus === 'draft' ||
      getters.bookingStatus === 'requested' ||
      getters.bookingStatus === 'refused'
    );
  },

  showRefuseButton: (state: BookingFormState, getters): boolean => {
    return getters.bookingStatus === 'requested';
  },

  showReplaceButton: (state: BookingFormState, getters): boolean => {
    return state.showReplaceButton && getters.showConflicts;
  },

  showLargeDrawer: (state: BookingFormState, getters): boolean => {
    return (
      getters.currentAction === 'edit' ||
      (!getters.isSimpleBooking && getters.currentAction === 'search')
    );
  },

  showConflicts: (state: BookingFormState): boolean => {
    return state.showConflicts;
  },

  // Action buttons state
  searchButtonDisabled: (state: BookingFormState, getters): boolean => {
    return getters.isSaving || getters.isSearching;
  },

  saveButtonDisabled: (state: BookingFormState, getters): boolean => {
    return getters.isSaving || getters.isSearching;
  },

  replaceButtonDisabled: (state: BookingFormState, getters): boolean => {
    return false;
  },

  // Getters
  cantSaveBookingMessages: (state: BookingFormState, getters): string[] => {
    const result = [];

    if (!getters.formHasStartAndEndTime) {
      result.push('bookings.cant_save_reasons.missing_start_and_end_time');
    }
    if (!getters.bookingStartDate) {
      result.push('bookings.cant_save_reasons.missing_start_date');
    }
    if (getters.repetitive && !getters.bookingEndDate) {
      result.push('bookings.cant_save_reasons.missing_end_date');
    }
    if (getters.type === 'booking' && !getters.contactId && !state.model.contact_id) {
      result.push('bookings.cant_save_reasons.missing_contact');
    }
    // if (getters.type === 'booking' && !getters.priceCategoryId && !state.model.price_category_id) {
    //   result.push('bookings.cant_save_reasons.missing_price_category');
    // }
    if (getters.resources.length < 1) {
      result.push('bookings.cant_save_reasons.missing_resource');
    }
    if (getters.type === 'booking' && getters.price < 0) {
      result.push('bookings.cant_save_reasons.negative_price');
    }

    return result;
  },

  cantSearchBookingMessages: (state: BookingFormState, getters): string[] => {
    const result = [];

    if (!getters.formHasStartAndEndTime) {
      result.push('bookings.cant_search_reasons.missing_start_and_end_time');
    }
    if (!getters.bookingStartDate) {
      result.push('bookings.cant_search_reasons.missing_start_date');
    }
    if (getters.repetitive && !getters.bookingEndDate) {
      result.push('bookings.cant_search_reasons.missing_end_date');
    }
    if (getters.type === 'booking' && !getters.contactId) {
      result.push('bookings.cant_search_reasons.missing_contact');
    }
    if (getters.resources.length < 1 && !getters.discipline) {
      result.push('bookings.cant_search_reasons.missing_resource_or_discipline');
    }

    return result;
  },
  type: (state: BookingFormState): string => {
    return state.model?.booking_type;
  },

  repetitive: (state: BookingFormState): boolean => {
    return !!Number(state.model?.repetitive);
  },

  resources: (state: BookingFormState): Resource[] => {
    return state.model?.resources || [];
  },
  contactSubscriptions: (state: BookingFormState) => {
    return state.model?.contact_subscriptions;
  },

  resourceIds: (state: BookingFormState): number[] => {
    return (state.model?.resources || []).reduce((ids: number[], resource: Resource) => {
      ids.push(Number(resource.id));
      return ids;
    }, []);
  },

  bookingEvents: (state: BookingFormState): BookingEvent[] => {
    return state.model?.bookingEvents || [];
  },

  conflicts: (state: BookingFormState, getters): BookingEvent[] => {
    return getters.bookingEvents.reduce((conflicts: BookingEvent[], bookingEvent: BookingEvent) => {
      const newConflicts = bookingEvent.conflicts;
      return conflicts.concat(newConflicts);
    }, []);
  },

  bookingStartTime: (state: BookingFormState): string => {
    return state.model?.start_time || state.model?.time_range?.start_time;
  },

  bookingStatus: (state: BookingFormState): string => {
    return state.model?.status;
  },

  bookingEndTime: (state: BookingFormState): string => {
    return state.model?.end_time || state.model?.time_range?.end_time;
  },

  bookingStartDate: (state: BookingFormState): string => {
    return state.model?.start_date;
  },

  bookingEndDate: (state: BookingFormState): string => {
    return state.model?.end_date;
  },

  contact: (state: BookingFormState): Contact => {
    return state.model?.contact;
  },

  contactId: (state: BookingFormState, getters): number => {
    return getters?.contact?.id;
  },

  contactPriceCategory: (state: BookingFormState, getters): number => {
    return getters?.contact?.price_category;
  },
  contactPriceCategoryId: (state: BookingFormState, getters): number => {
    return getters.contactPriceCategory?.id ? Number(getters.contactPriceCategory?.id) : null;
  },

  priceCategory: (state: BookingFormState): PriceCategory => {
    return state.model?.price_category;
  },
  priceCategoryId: (state: BookingFormState, getters): number => {
    return getters.priceCategory?.id ? Number(getters.priceCategory?.id) : null;
  },

  discipline: (state: BookingFormState): Discipline => {
    return state.model?.discipline;
  },

  disciplineId: (state: BookingFormState): number => {
    return state.model?.discipline?.id;
  },

  selectedSuggestedArticles: (state: BookingFormState): Record<string, string[]> => {
    // return state.model?.aditionalArticles || [];
    return state.model?.selected_suggested_articles;
  },

  resourcesSelectInfo: (state: BookingFormState): ResourceSelectInfo[] => {
    // return state.model?.aditionalArticles || [];
    return state.resourcesSelectInfo || [];
  },

  dateRange: (state: BookingFormState): DateRange => {
    // return state.model?.aditionalArticles || [];
    return state.model?.date_range || { start_date: null, end_date: null };
  },

  price: (state: BookingFormState): number => {
    return Number(state.model?.booking_price);
  },

  days: (state: BookingFormState): DaysInput => {
    return (
      state.model?.days || {
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false,
      }
    );
  },

  timeRange: (state: BookingFormState): TimeRange => {
    // return state.model?.aditionalArticles || [];
    return state.model?.time_range || { start_time: null, end_time: null };
  },

  reductionArticles: (state: BookingFormState): Article[] => {
    return state.model?.reduction_articles;
  },

  article: (state): Article => {
    return state.model?.article;
  },
  articleId: (state, getters): number => {
    return getters.article?.id ? Number(getters.article?.id) : null;
  },

  // endDate,
  // startDate,
  // startTime,
  // endTime,
  // contact,
  // discipline,
  // additional_articles
};

const actions: BookingFormActions = {
  ...abstract_actions,
  calcPrice: async ({ commit, getters }) => {
    commit('setCheckingPrice', true);
    const basePath = bpcRouterHelper.getEntityPath('booking');

    const booking: Booking = getters.model;
    booking.start_time = booking.time_range.start_time;
    booking.end_time = booking.time_range.end_time;
    booking.contact_id = booking.contact ? booking.contact.id : null;
    booking.price_category_id = booking.price_category ? booking.price_category.id : null;
    booking.discipline_id = booking.discipline ? booking.discipline.id : null;
    // NOTE: Get price only if all required fields are filled
    if (booking.start_time && booking.end_time && booking.contact_id) {
      try {
        let res;
        if (getters.repetitive) {
          res = await bpcApi.post(basePath + '/get-repetitive-price', booking).toPromise();
          commit('setModelProp', { prop: 'bookingEvents', value: res['bookingEvents'] });
        } else {
          res = await bpcApi.post(basePath + '/get-simple-price', booking).toPromise();
        }
        commit('setModelProp', {
          prop: 'booking_price',
          value: res['booking_price'].toFixed(2),
        });
        commit('setCheckingPrice', false); // TODO
        commit('setBookingFormModelChanged', false);
        return res['booking_price'].toFixed(2);
      } catch (error) {
        commit('setCheckingPrice', false); // TODO
        bpcMessage.showErrorMessage({ text: 'global.generic.error_form_action' });
        throw error;
      }
    }
  },

  resetForm: async ({ commit, getters }): Promise<void> => {
    commit('setInitialState');
    commit('setBookingInitialState');
    commit('setInitialModel', { fields: getters.fieldsObject, model: null });
  },

  calculateResourcesSelectInfo: async ({ commit, getters }) => {
    let resource_id = null;
    const resourcesSelectInfo: ResourceSelectInfo[] = [];
    let newElement: ResourceSelectInfo = null;

    getters.bookingEvents.forEach((bookingEvent, k) => {
      if (resource_id != bookingEvent.resource_id) {
        resource_id = bookingEvent.resource_id;
        if (newElement) {
          resourcesSelectInfo.push(newElement);
        }
        newElement = { resource: bookingEvent.resource, withoutConflicts: 0, bookingEvents: [] };
      }

      if (!bookingEvent.conflicts.length) {
        newElement.withoutConflicts++;
      }
      newElement.bookingEvents.push(bookingEvent);

      if (k === getters.bookingEvents.length - 1) {
        // If is the last element then we put the newElement in resourcesSelectInfo
        resourcesSelectInfo.push(newElement);
      }
    });

    commit('setResourcesSelectInfo', resourcesSelectInfo);
  },
  // extends with new prop and methods here
};

const mutations: BookingFormMutations = {
  ...abstract_mutations,
  setCheckingPrice: (state: BookingFormState, value: boolean) => {
    state.checkingPrice = value;
  },
  setBookingFormModelChanged: (state: BookingFormState, value: boolean) => {
    state.bookingFormModelChanged = value;
  },
  setIsSearched: (state: BookingFormState, value: boolean) => {
    state.isSearched = value;
  },
  setShowReplaceButton: (state: BookingFormState, value: boolean) => {
    state.showReplaceButton = !$authorizationService.hasPermission('replace_bookings')
      ? false
      : value;
  },
  setShowConflicts: (state: BookingFormState, value: boolean) => {
    state.showConflicts = value;
  },
  setResourcesSelectInfo: (state: BookingFormState, value: ResourceSelectInfo[]) => {
    state.resourcesSelectInfo = value;
  },
  setBookingInitialState: (state: BookingFormState) => {
    state.conflictChanged = false;
    state.eventChanged = false;
    state.checkingPrice = false;
    state.bookingFormModelChanged = false;
    state.isSearched = false;
    state.showReplaceButton = false;
    state.showConflicts = false;
    state.resourcesSelectInfo = [];
  },

  setWeekDay: (state: BookingFormState, start_date: string): void => {
    // Initialize the weekday based on the start date
    const date = moment(start_date);
    const weekday = date
      .format('dddd')
      .substring(0, 3)
      .toLowerCase();
    const days = {
      mon: 'mon' === weekday,
      tue: 'tue' === weekday,
      wed: 'wed' === weekday,
      thu: 'thu' === weekday,
      fri: 'fri' === weekday,
      sat: 'sat' === weekday,
      sun: 'sun' === weekday,
    };
    setProp(state, 'model.days', days);
  },
  // extends with new prop and methods here
};

export const BookingModule: Module<BookingFormState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
